import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { useToast } from '../context/ToastContext';

const SearchCompanies = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const runSearch = async (term, signal) => {
    try {
      const response = await api.get('/search/companies', {
        params: { q: term },
        signal,
      });
      setCompanies(response.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error(err);
      showToast(t('searchCompanies.searchError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Live search: debounce 300ms după ultima apăsare. Cere minim 2 caractere pentru
  // a evita request-uri inutile pe un singur caracter.
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) {
      setCompanies([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setSearched(true);
    const timer = setTimeout(() => {
      runSearch(term, controller.signal);
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleSearch = (e) => {
    // Submit manual (Enter / click) — forțează căutarea imediată fără debounce.
    e.preventDefault();
    const term = searchTerm.trim();
    if (term.length < 2) {
      showToast(t('searchCompanies.enterSearchTerm'), 'error');
      return;
    }
    setLoading(true);
    setSearched(true);
    runSearch(term);
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('searchCompanies.title')} subtitle={t('searchCompanies.subtitle')} />

      {/* Search Form */}
      <form onSubmit={handleSearch}>
        <Card>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('searchCompanies.placeholder')}
                className="input pl-10"
              />
            </div>
            <Button type="submit" variant="primary" size="md" loading={loading}>
              {loading ? t('searchCompanies.searching') : t('common.search')}
            </Button>
          </div>
        </Card>
      </form>

      {/* Results */}
      {searched && (
        <Card>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            {t('searchCompanies.resultsTitle')} ({companies.length})
          </h2>

          {loading ? (
            <Spinner label={t('searchCompanies.searching')} />
          ) : companies.length === 0 ? (
            <EmptyState
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              }
              title={t('searchCompanies.noCompanyFound')}
              description={t('searchCompanies.tryOtherTerms')}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {companies.map((company) => (
                <Link
                  key={company.id}
                  to={`/search/company/${company.id}`}
                  className="border-2 border-slate-200 dark:border-slate-800 rounded-lg p-4 hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-slate-800 transition group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-lg group-hover:text-primary-700 transition-colors">
                        {company.name}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        CUI: {company.cui}
                      </p>
                    </div>
                    <Badge color={company.active ? 'green' : 'red'}>
                      {company.active ? t('searchCompanies.active') : t('searchCompanies.inactive')}
                    </Badge>
                  </div>

                  {company.address && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4 text-slate-400 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      {company.address}
                    </p>
                  )}

                  <div className="flex gap-4 text-sm text-slate-500 dark:text-slate-400 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <span className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4 text-slate-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      {company._count.employees} {t('searchCompanies.employeesLabel')}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4 text-slate-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                      {company._count.assignments} {t('searchCompanies.assignmentsLabel')}
                    </span>
                  </div>

                  <div className="mt-3 text-primary-600 dark:text-primary-300 font-medium text-sm flex items-center gap-1">
                    {t('searchCompanies.viewDetails')}
                    <svg
                      className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default SearchCompanies;
